import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import ResetPassword from './pages/Auth/ResetPassword';
import Dashboard from './pages/Dashboard/Dashboard';
import ClientList from './pages/Clients/ClientList';
import ClientDetail from './pages/Clients/ClientDetail';
import ClientForm from './pages/Clients/ClientForm';
import FacilityList from './pages/Facilities/FacilityList';
import FacilityDetail from './pages/Facilities/FacilityDetail';
import FacilityForm from './pages/Facilities/FacilityForm';
import PlacementList from './pages/Placements/PlacementList';
import PlacementDetail from './pages/Placements/PlacementDetail';
import PlacementForm from './pages/Placements/PlacementForm';
import SwimLaneTracker from './pages/Placements/SwimLaneTracker';
import TaskList from './pages/Tasks/TaskList';
import TaskForm from './pages/Tasks/TaskForm';
import ResourceList from './pages/Resources/ResourceList';
import ResourceDetail from './pages/Resources/ResourceDetail';
import ResourceUpload from './pages/Resources/ResourceUpload';
import ResourceEdit from './pages/Resources/ResourceEdit';
import UIPolishDemo from './pages/UIPolishDemo';
import LoadingSpinner from './components/UI/LoadingSpinner';
import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirect to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <Router>
            <div className="App min-h-screen bg-gray-50">
              <Routes>
                {/* Public routes */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <PublicRoute>
                      <ResetPassword />
                    </PublicRoute>
                  }
                />

                {/* Protected routes */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Routes>
                          <Route path="/dashboard" element={<Dashboard />} />
                          
                          {/* Client routes */}
                          <Route path="/clients" element={<ClientList />} />
                          <Route path="/clients/new" element={<ClientForm />} />
                          <Route path="/clients/:id/edit" element={<ClientForm />} />
                          <Route path="/clients/:id" element={<ClientDetail />} />
                          
                          {/* Facility routes */}
                          <Route path="/facilities" element={<FacilityList />} />
                          <Route path="/facilities/new" element={<FacilityForm />} />
                          <Route path="/facilities/:id/edit" element={<FacilityForm />} />
                          <Route path="/facilities/:id" element={<FacilityDetail />} />
                          
                          {/* Placement routes */}
                          <Route path="/placements" element={<PlacementList />} />
                          <Route path="/placements/new" element={<PlacementForm />} />
                          <Route path="/placements/:id/edit" element={<PlacementForm />} />
                          <Route path="/placements/:id" element={<PlacementDetail />} />
                          <Route path="/swim-lanes" element={<SwimLaneTracker />} />
                          
                          {/* Task routes */}
                          <Route path="/tasks" element={<TaskList />} />
                          <Route path="/tasks/new" element={<TaskForm />} />
                          <Route path="/tasks/:id/edit" element={<TaskForm />} />
                          
                          {/* Resource routes */}
                          <Route path="/resources" element={<ResourceList />} />
                          <Route path="/resources/upload" element={<ResourceUpload />} />
                          <Route path="/resources/:id/edit" element={<ResourceEdit />} />
                          <Route path="/resources/:id" element={<ResourceDetail />} />
                          
                          {/* UI Polish Demo */}
                          <Route path="/ui-demo" element={<UIPolishDemo />} />
                          
                          {/* Default redirect */}
                          <Route path="/" element={<Navigate to="/dashboard" replace />} />
                          <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </div>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
